import { Injectable } from '@angular/core';
import {
  HttpInterceptor, HttpRequest, HttpHandler,
  HttpEvent, HttpErrorResponse
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/services/auth.service';
import { AuthStateService } from './auth-state.service';
import { TokenService } from './token.service';
import { StorageIntegrityService } from './storage-integrity.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  static readonly RETRY_HEADER = 'X-Token-Refresh-Retry';

  private isLoggingOut = false;

  private readonly SKIP_AUTH_URLS = [
    '/api/CAS/login',
    '/api/CAS/signout',
    '/api/CAS/tokenRefresh',
    '/companies/user/change/password',
    '/api/Authorization/RefreshToken'
  ];

  constructor(
    private authService: AuthService,
    private authStateService: AuthStateService,
    private tokenService: TokenService,
    private storageIntegrityService: StorageIntegrityService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.shouldSkipAuth(req.url)) {
      return next.handle(req);
    }

    // Validate storage integrity before every authenticated request (10s cache)
    return from(this.storageIntegrityService.validateIntegrity()).pipe(
      switchMap(isValid => {
        if (!isValid) {
          this.storageIntegrityService.handleTampering();
          return throwError(() => new Error('Storage integrity violation'));
        }

        const authedReq = this.addAuthHeader(req);
        return next.handle(authedReq).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
              return this.handle401Error(authedReq, next);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private addAuthHeader(req: HttpRequest<any>): HttpRequest<any> {
    let modifiedReq = req;

    // Get company first since userDetails key depends on it
    const company = this.authStateService.currentCompany ||
                    localStorage.getItem('currentCompany');

    // Get userID from authStateService or fallback to localStorage
    const user = this.authStateService.user;
    let userID = user?.userID;
    
    // Fallback: try to get userID from localStorage if not in state
    if (!userID) {
      try {
        // Try ngx-webstorage key first (tibss.userDetails)
        let storedUser = localStorage.getItem('tibss.userDetails');
        
        // Fallback to company-specific key
        if (!storedUser && company) {
          storedUser = localStorage.getItem(`tibss.${company}-userDetails`);
        }
        
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          userID = parsedUser?.userID;
        }
      } catch (e) {
        console.warn('[TokenInterceptor] Failed to parse user from localStorage');
      }
    }

    // Inject userID into request body for POST/PUT/PATCH/DELETE if body exists
    if (userID && req.body && typeof req.body === 'object' && !(req.body instanceof FormData) && !req.body.userID) {
      modifiedReq = req.clone({
        body: { ...req.body, userID }
      });
    }

    // Inject userID as query param for GET/DELETE without body
    if (userID && (req.method === 'GET' || (req.method === 'DELETE' && !req.body)) && !req.params.has('userID')) {
      modifiedReq = (modifiedReq === req ? req : modifiedReq).clone({
        params: (modifiedReq === req ? req : modifiedReq).params.set('userID', String(userID))
      });
    }

    // Add auth headers if available
    if (!company) return modifiedReq;

    const token = this.tokenService.getToken(company);
    if (!token) return modifiedReq;

    return modifiedReq.clone({
      setHeaders: {
        'Authorization': `Bearer ${token}`,
        'X-Company': company
      }
    });
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If this is already a retry, the refresh token is also expired — logout
    if (req.headers.get(TokenInterceptor.RETRY_HEADER) === '1') {
      console.warn('[TokenInterceptor] Retry request also got 401 — session expired');
      this.doLogout();
      return throwError(() => new Error('Session expired'));
    }

    const company = this.authStateService.currentCompany ||
                    localStorage.getItem('currentCompany');
    if (!company) {
      this.doLogout();
      return throwError(() => new Error('No active company'));
    }

    return from(this.tokenService.refreshToken(company)).pipe(
      switchMap(() => {
        // Retry original request once with the new token
        const retryReq = req.clone({
          setHeaders: { [TokenInterceptor.RETRY_HEADER]: '1' }
        });
        return next.handle(this.addAuthHeader(retryReq));
      }),
      catchError(refreshError => {
        // Network/abort errors are transient — don't logout
        const isNetworkError =
          refreshError?.name  === 'AbortError' ||
          refreshError?.status === 0;

        if (isNetworkError) {
          console.warn('[TokenInterceptor] Network error during refresh — keeping session');
          return throwError(() => refreshError);
        }

        console.error('[TokenInterceptor] Token refresh failed — logging out', refreshError);
        this.doLogout();
        return throwError(() => new Error('Session expired'));
      })
    );
  }

  private doLogout(): void {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;
    this.authService.logout().finally(() => {
      setTimeout(() => { this.isLoggingOut = false; }, 5000);
    });
  }

  private shouldSkipAuth(url: string): boolean {
    return this.SKIP_AUTH_URLS.some(skip => url.includes(skip));
  }
}
